import React, { useEffect, useState, useRef, useContext } from 'react'
import * as header from './headers'
import * as footer from './footers'
import * as formTemplate from './templates'
import { useParams } from 'react-router-dom'
import Conditional from '../common/Conditional'
import FormErrorDisplay from '../common/FormErrorMessages'
import useErrorHandler from '../../hooks/useErrorHandler'
import InteractiveTemplateService from '../../services/InteractiveTemplateService'
import { getFields } from './common'
import { LoaderContext } from '../context/FormLoader'
import {
  FormKey,
  IInteractiveTemplateGetFormResponse,
  IInteractiveTemplatePostFormRequest,
  IInteractiveTemplatePostFormRequestResponse,
  IInteractiveTemplateField,
} from '../../models/InteractiveTemplate'
import './InteractiveTemplates.css'

const InteractiveTemplateMain = () => {
  const { id } = useParams()
  const [userErrors, handleErrors] = useErrorHandler()
  const [isLoading, setIsLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [showHeader, setShowHeader] = useState(true)
  const [expandedFooter, setExpandedFooter] = useState(false)
  const [showRequestForRecordsFooter, setShowRequestForRecordsFooter] =
    useState(false)
  const [showProbationFooter, setShowProbationFooter] = useState(false)
  const [showResignationFooter, setShowResignationFooter] = useState(false)
  const [showWarningFooter, setShowWarningFooter] = useState(false)
  const [showNoticeFooter, setShowNoticeFooter] = useState(false)
  const [showAgreementFooter, setShowAgreementFooter] = useState(false)
  const [formValues, setFormValues] = useState()
  const [fields, setFields] = useState<IInteractiveTemplateField[]>()
  const [template, setTemplate] =
    useState<IInteractiveTemplateGetFormResponse>()
  const mountedRef = useRef(false)
  const loader = useContext(LoaderContext)
  const fileLoader = useContext(LoaderContext)

  useEffect(() => {
    mountedRef.current = true
    loader.up()
    if (id) {
      setShowHeader(true)
      setShowForm(false)
      setExpandedFooter(false)
      InteractiveTemplateService.getForm(id)
        .then((response) => {
          if (mountedRef.current) {
            setTemplate(response.result)
            setFields(getFields(response.result))
          }
        })
        .catch((error) => {
          handleErrors(error)
          window.scrollTo(0, 0)
        })
        .finally(() => {
          if (mountedRef.current) {
            setIsLoading(false)
            loader.down()
          }
        })
    } else {
      handleErrors(['The document tamplate has not been found'])
      window.scrollTo(0, 0)
    }

    return () => {
      mountedRef.current = false
    }
  }, [id])

  const renderHeader = (template: IInteractiveTemplateGetFormResponse) => {
    switch (template?.formKey) {
      case FormKey.EndOfProbation:
        return (
          <header.EndOfProbationHeader
            handleStart={handleStart}
            show={showHeader}
            setShow={() => setShowHeader(!showHeader)}
          ></header.EndOfProbationHeader>
        )

      case FormKey.AnnualLeaveNotice:
        return (
          <header.AnnualLeaveNoticeHeader
            handleStart={handleStart}
            show={showHeader}
            setShow={() => setShowHeader(!showHeader)}
          ></header.AnnualLeaveNoticeHeader>
        )

      case FormKey.LetterOfResignation:
        return (
          <header.LetterOfResignationHeader
            handleStart={handleStart}
            show={showHeader}
            setShow={() => setShowHeader(!showHeader)}
          ></header.LetterOfResignationHeader>
        )

      case FormKey.WarningLetter:
        return (
          <header.WarningLetterHeader
            handleStart={handleStart}
            show={showHeader}
            setShow={() => setShowHeader(!showHeader)}
          ></header.WarningLetterHeader>
        )

      case FormKey.RequestForRecords:
        return (
          <header.RequestForRecordsHeader
            handleStart={handleStart}
            show={showHeader}
            setShow={() => setShowHeader(!showHeader)}
          ></header.RequestForRecordsHeader>
        )

      case FormKey.AnnualLeaveAgreement:
        return (
          <header.AnnualLeaveAgreementHeader
            handleStart={handleStart}
            show={showHeader}
            setShow={() => setShowHeader(!showHeader)}
          ></header.AnnualLeaveAgreementHeader>
        )
    }
  }

  const renderTemplate = (template: IInteractiveTemplateGetFormResponse) => {
    switch (template?.formKey) {
      case FormKey.EndOfProbation:
        return (
          <formTemplate.EndOfProbationTemplate
            template={template}
            handleNext={handleNext}
            show={showForm}
            setShow={(show) => setShowForm(show ?? !showForm)}
          ></formTemplate.EndOfProbationTemplate>
        )

      case FormKey.LetterOfResignation:
        return (
          <formTemplate.LetterOfResignationTemplate
            template={template}
            handleNext={handleNext}
            show={showForm}
            setShow={(show) => setShowForm(show ?? !showForm)}
          ></formTemplate.LetterOfResignationTemplate>
        )

      case FormKey.WarningLetter:
        return (
          <formTemplate.WarningLetterTemplate
            template={template}
            handleNext={handleNext}
            show={showForm}
          ></formTemplate.WarningLetterTemplate>
        )

      case FormKey.RequestForRecords:
        return (
          <formTemplate.RequestForRecordsTemplate
            template={template}
            handleNext={handleNext}
            show={showForm}
          ></formTemplate.RequestForRecordsTemplate>
        )

      case FormKey.AnnualLeaveNotice:
        return (
          <formTemplate.AnnualLeaveNoticeTemplate
            template={template}
            handleNext={handleNext}
            show={showForm}
          ></formTemplate.AnnualLeaveNoticeTemplate>
        )

      case FormKey.AnnualLeaveAgreement:
        return (
          <formTemplate.AnnualLeaveAgreementTemplate
            template={template}
            handleNext={handleNext}
            show={showForm}
          ></formTemplate.AnnualLeaveAgreementTemplate>
        )
    }
  }

  const handleStart = () => {
    setShowHeader(false)
    setShowForm(true)
    setExpandedFooter(false)
    window.scrollTo(0, 0)
  }

  const handleNext = async (values, formId) => {
    setFormValues(values)
    setShowHeader(false)
    setShowForm(false)
    //setShowFooter(true)
    switch (formId) {
      case FormKey.RequestForRecords:
        setShowRequestForRecordsFooter(true)
        break
      case FormKey.EndOfProbation:
        setShowProbationFooter(true)
        break
      case FormKey.LetterOfResignation:
        setShowResignationFooter(true)
        break
      case FormKey.WarningLetter:
        setShowWarningFooter(true)
        break
      case FormKey.AnnualLeaveNotice:
        setShowNoticeFooter(true)
        break
      case FormKey.AnnualLeaveAgreement:
        setShowAgreementFooter(true)
        break
    }
    setExpandedFooter(true)
    window.scrollTo(0, 0)
  }

  const handleFooter = () => {
    setExpandedFooter(!expandedFooter)
  }

  const handleDownload = () => {
    fileLoader.message = 'Generating letter'
    fileLoader.up()
    if (formValues['HOW_WARNING']) {
      formValues['HOW_WARNING'] = formValues['HOW_WARNING']
        .toString()
        .toLowerCase()
    }
    const request = buildPostFormRequest(formValues)
    const firstName =
      formValues && formValues['LETTER_FIRSTNAME']
        ? formValues['LETTER_FIRSTNAME']
        : 'name'
    const lastName =
      formValues && formValues['LETTER_LASTNAME']
        ? formValues['LETTER_LASTNAME']
        : 'surname'
    InteractiveTemplateService.postForm(request)
      .then((response) => {
        // Create blob link to download
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', firstName + ' ' + lastName + '.docx')

        // Start download
        link.click()
      })
      .catch((error) => {
        handleErrors(error)
        window.scrollTo(0, 0)
      })
      .finally(() => {
        fileLoader.down()
      })
  }

  const buildPostFormRequest = (formValues) => {
    if (formValues) {
      const responses: IInteractiveTemplatePostFormRequestResponse[] = []
      for (const key of Object.keys(formValues)) {
        const field = fields.find(
          (item: IInteractiveTemplateField) => item.replacement === key
        )

        let values =
          key === 'RECORD_REQUEST' ? formValues[key] : [formValues[key]]

        if (field?.prerequisite) {
          if (field.replacement !== 'OTHER_RECORDS') {
            if (
              field.replacement === 'WARNING_ONE' ||
              field.replacement === 'WARNING_MORE'
            ) {
              if (
                !field.expected
                  ?.split('|')
                  .includes(formValues[field.prerequisite])
              ) {
                values = ['']
              }
            } else if (field.expected !== formValues[field.prerequisite]) {
              values = ['']
            }
          }
        }

        const response: IInteractiveTemplatePostFormRequestResponse = {
          name: key,
          value: values,
          type: 'text',
          column: null,
          postcontent: field?.postContent,
          precontent: field?.preContent,
          tablename: null,
        }
        responses.push(response)
      }
      const request: IInteractiveTemplatePostFormRequest = {
        formKey: id,
        responses: responses,
      }
      return request
    }
  }

  return (
    <>
      <FormErrorDisplay
        show={userErrors?.length > 0}
        errorsList={userErrors}
        heading="Please review the following errors"
      />
      <Conditional if={!isLoading && template}>
        <>
          {renderHeader(template)}
          {renderTemplate(template)}
          {/* <Conditional if={showFooter}>{renderFooter(template)}</Conditional> */}
          <Conditional
            if={id === FormKey.RequestForRecords && showRequestForRecordsFooter}
          >
            <footer.RequestForRecordsFooter
              handleDownload={handleDownload}
              show={expandedFooter}
              setShow={handleFooter}
            ></footer.RequestForRecordsFooter>
          </Conditional>

          <Conditional
            if={id === FormKey.EndOfProbation && showProbationFooter}
          >
            <footer.EndOfProbationFooter
              handleDownload={handleDownload}
              expanded={expandedFooter}
              setShow={handleFooter}
            ></footer.EndOfProbationFooter>
          </Conditional>

          <Conditional
            if={id === FormKey.LetterOfResignation && showResignationFooter}
          >
            <footer.LetterOfResignationFooter
              handleDownload={handleDownload}
              expanded={expandedFooter}
              setShow={handleFooter}
            ></footer.LetterOfResignationFooter>
          </Conditional>

          <Conditional if={id === FormKey.WarningLetter && showWarningFooter}>
            <footer.WarningLetterFooter
              handleDownload={handleDownload}
              show={expandedFooter}
              setShow={handleFooter}
            ></footer.WarningLetterFooter>
          </Conditional>

          <Conditional
            if={id === FormKey.AnnualLeaveNotice && showNoticeFooter}
          >
            <footer.AnnualLeaveNoticeFooter
              handleDownload={handleDownload}
              show={expandedFooter}
              setShow={handleFooter}
            ></footer.AnnualLeaveNoticeFooter>
          </Conditional>

          <Conditional
            if={id === FormKey.AnnualLeaveAgreement && showAgreementFooter}
          >
            <footer.AnnualLeaveAgreementFooter
              handleDownload={handleDownload}
              show={expandedFooter}
              setShow={handleFooter}
            ></footer.AnnualLeaveAgreementFooter>
          </Conditional>
        </>
      </Conditional>
    </>
  )
}

export default InteractiveTemplateMain
